"use client";

import { usePathname } from "next/navigation";
import { useMemo, useState } from "react";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { FaqRichText } from "@/components/faq/faq-richtext";
import { useFaqDrawer } from "@/lib/faq-context";
import { topicLabel } from "@/lib/faq-topics";
import { topicSlug, topicUrl, type FaqStory } from "@/lib/faqs";
import { regionFromPathname, regionPath } from "@/lib/region";

type AnswerNode = React.ComponentProps<typeof FaqRichText>["node"];

type BlogFaqItemBlok = SbBlokData & {
  question?: string;
  answer?: AnswerNode;
};

type BlogFaqQuestionsBlok = SbBlokData & {
  title?: string;
  description?: string;
  topic?: string;
  max_questions?: string | number;
  questions?: string[];
  custom_questions?: BlogFaqItemBlok[];
};

type Item = {
  id: string;
  question: string;
  answer: AnswerNode;
  blok?: SbBlokData;
};

function ArrowIcon() {
  return (
    <svg width="17" height="11" viewBox="0 0 17 11" fill="none" aria-hidden>
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function BlogFaqQuestions({
  blok,
}: {
  blok: BlogFaqQuestionsBlok;
}) {
  const { faqs: contextFaqs } = useFaqDrawer();
  const pathname = usePathname();
  const region = regionFromPathname(pathname);

  const limit =
    typeof blok.max_questions === "string"
      ? parseInt(blok.max_questions, 10) || 0
      : blok.max_questions ?? 0;
  const targetTopic = blok.topic ? topicSlug(blok.topic) : "";

  const pulled = useMemo<FaqStory[]>(() => {
    if (targetTopic) {
      return contextFaqs.filter(
        (f) => topicSlug(f.content?.topic) === targetTopic,
      );
    }
    if (Array.isArray(blok.questions) && blok.questions.length > 0) {
      const byUuid = new Map(contextFaqs.map((f) => [f.uuid, f]));
      return blok.questions
        .map((uuid) => byUuid.get(uuid))
        .filter((f): f is FaqStory => f !== undefined);
    }
    return [];
  }, [targetTopic, blok.questions, contextFaqs]);

  const pulledLimited = useMemo(
    () => (limit > 0 ? pulled.slice(0, limit) : pulled),
    [pulled, limit],
  );

  const items = useMemo<Item[]>(() => {
    const custom = (blok.custom_questions ?? [])
      .filter((c) => c.question)
      .map<Item>((c) => ({
        id: c._uid as string,
        question: c.question as string,
        answer: c.answer,
        blok: c,
      }));
    const referenced = pulledLimited.map<Item>((f) => ({
      id: f.uuid,
      question: f.name,
      answer: f.content?.answer as AnswerNode,
    }));
    return [...custom, ...referenced];
  }, [blok.custom_questions, pulledLimited]);

  const hasMore = pulledLimited.length < pulled.length;
  const showLink = Boolean(targetTopic || blok.questions?.length);
  const linkHref =
    targetTopic && hasMore
      ? topicUrl(targetTopic, region)
      : regionPath("/faqs", region);
  const linkLabel =
    targetTopic && hasMore
      ? `View all ${topicLabel(targetTopic)} questions`
      : targetTopic
        ? "See other FAQ topics"
        : "Browse all FAQs";

  const [openId, setOpenId] = useState<string | null>(items[0]?.id ?? null);

  if (items.length === 0) return null;

  return (
    <div {...storyblokEditable(blok)} className="my-8 flex flex-col gap-5">
      {blok.title && (
        <h2 className="text-[clamp(2.25rem,4vw,3rem)] font-bold leading-[1.08] text-purple-05">
          {blok.title}
        </h2>
      )}
      {blok.description && (
        <p className="text-body leading-[1.56] text-[#5c5c66]">
          {blok.description}
        </p>
      )}

      <div className="flex flex-col">
        {items.map((item, i) => {
          const isOpen = openId === item.id;
          const isFirst = i === 0;
          const isLast = i === items.length - 1;
          return (
            <div
              key={item.id}
              {...(item.blok ? storyblokEditable(item.blok) : {})}
              className={`border-t ${
                isFirst ? "border-purple-02" : "border-purple-01"
              } ${isLast ? "border-b border-b-purple-01" : ""}`}
            >
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : item.id)}
                aria-expanded={isOpen}
                className="flex w-full cursor-pointer items-start justify-between gap-6 py-5 text-left"
              >
                <span className="flex-1 text-body font-bold leading-[1.56] text-[#212126] lg:text-h6">
                  {item.question}
                </span>
                <span
                  aria-hidden
                  className="shrink-0 text-[28px] leading-none text-purple-03"
                >
                  {isOpen ? "–" : "+"}
                </span>
              </button>
              <div
                className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                  isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                }`}
              >
                <div className="overflow-hidden">
                  <div className="pb-8 text-body leading-[1.56] text-[#5c5c66]">
                    <FaqRichText node={item.answer} />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showLink && (
        <a
          href={linkHref}
          className="inline-flex w-fit items-center gap-[7px] py-2.5 text-body font-extrabold leading-[1.054] text-purple-04 hover:underline"
        >
          {linkLabel}
          <ArrowIcon />
        </a>
      )}
    </div>
  );
}
